import useSWR, { useSWRConfig } from 'swr';

import myShopClient from '@services/nearlive';

/**
 * Generic insights fetcher. The Lovable prototype distinguishes between
 * Local Listings (organic) and Local Ads (paid) — we expose them as
 * separate hooks so pages can subscribe to only what they show.
 */
async function fetchInsights(path, params) {
	const client = await myShopClient();
	const { data } = await client.get(path, { params });
	return data;
}

export function useLocalListings(shopId, { from, to } = {}) {
	const key = shopId ? ['unify:local-listings', shopId, from, to] : null;
	const { data, error, isLoading } = useSWR(key, () => fetchInsights(`/api/local-listings/${shopId}`, { start: from, end: to }), {
		revalidateOnFocus: false
	});
	return { data, error, isLoading };
}

export function useLocalAds(shopId, { from, to } = {}) {
	const key = shopId ? ['unify:local-ads', shopId, from, to] : null;
	const { data, error, isLoading, mutate } = useSWR(key, () => fetchInsights(`/api/local-ads/${shopId}`, { start: from, end: to }), {
		revalidateOnFocus: false
	});
	const { mutate: globalMutate } = useSWRConfig();

	/**
	 * Persist a new daily budget via the existing local-ads plan endpoint.
	 * On success, revalidate this SWR cache so the page reflects the new
	 * `budget.daily` / `summary.monthlySpend` next render.
	 */
	const updateBudget = async (budget) => {
		const client = await myShopClient();
		await client.put(`/api/channels/local-ads/plan/${shopId}`, { budget });
		// Local Ads is the score's one dynamic weight — revalidate the gauge too.
		await Promise.all([mutate(), globalMutate(['local-potential', shopId])]);
	};

	/**
	 * Activate Local Inventory Ads from the empty-state CTA. Sequences the two
	 * mutations that have to land for the page to flip into the populated
	 * branch: enable the `local-ads` channel (creates the row in DynamoDB if
	 * absent), then create the initial upcoming plan at the suggested daily
	 * budget. The same pair of endpoints the retailers-dashboard hits when a
	 * user starts their first Local Ads plan — no new backend surface.
	 *
	 * SWR mutate fires last so `data.enabled` flips to true and the page
	 * automatically transitions from the EmptyState to the live KPIs without a
	 * route change or hard reload.
	 */
	const activate = async (budget = 5) => {
		const client = await myShopClient();
		await client.post(`/api/channels/${shopId}/local-ads/enable`);
		await client.put(`/api/channels/local-ads/plan/${shopId}`, { budget });
		// Both this hook (so the page flips to populated) and useChannels (so
		// the sidebar's "+ Learn more" pill flips to "Live"). Without the global
		// mutate the sidebar reads stale channel data until the next page
		// refresh. The Local Potential gauge revalidates too (LIA is a score input).
		await Promise.all([mutate(), globalMutate(['unify:channels', shopId]), globalMutate(['local-potential', shopId])]);
	};

	return { data, error, isLoading, mutate, updateBudget, activate };
}

/**
 * Cross-shop benchmarks for Local Inventory Ads — currently surfaces the
 * "Industry average" CTR caption on the Ads page. Cached for a week
 * server-side, so a single revalidation per session is fine.
 */
export function useLocalAdsAverages() {
	const { data, error, isLoading } = useSWR('unify:local-ads:averages', () => fetchInsights('/api/local-ads/average-results'), {
		revalidateOnFocus: false
	});
	return { data, error, isLoading };
}

/**
 * Last-12-months totals for an insights table, via the generic
 * `POST /api/insights` endpoint (same path the Inventory accuracy page uses
 * for its upload-history strip). Used by the channel manage page to surface
 * real Google Local Listings performance — daily rows are fetched and summed
 * client-side. Returns `hasData: false` when the table has no rows for the
 * shop, so the caller can omit the Performance tiles rather than show zeros.
 */
export function useInsightsTotals(shopId, table, days = 365) {
	const key = shopId && table ? ['unify:insights-totals', shopId, table, days] : null;
	const { data, error, isLoading } = useSWR(
		key,
		async () => {
			const client = await myShopClient();
			const to = new Date();
			const from = new Date(to.getTime() - days * 24 * 60 * 60 * 1000);
			const iso = (d) => d.toISOString().slice(0, 10);
			const { data } = await client.post('/api/insights', {
				id: [shopId],
				table,
				columns: ['date', 'impressions', 'clicks'],
				from: iso(from),
				to: iso(to),
				sort: [{ column: 'date', order: 'asc' }]
			});
			return data;
		},
		{ revalidateOnFocus: false }
	);
	const rows = Array.isArray(data) ? data : Array.isArray(data?.rows) ? data.rows : [];
	const sum = (column) => rows.reduce((total, row) => total + (Number(row[column]) || 0), 0);
	return { impressions: sum('impressions'), clicks: sum('clicks'), hasData: rows.length > 0, error, isLoading };
}

/**
 * Last-N-day Google Business Profile totals (`gmb` insights table), summed
 * client-side. Returns the same shape as `useInsightsTotals` but with GBP-
 * native column names rolled up into two figures:
 *   - `views` — search + map views (desktop + mobile) on the Business Profile
 *   - `interactions` — website + phone + directions clicks
 * Pair this with `useInsightsTotals(.., 'local_listings')` on the Google
 * channel page to surface the full organic Google footprint, not just the
 * Local Inventory subset (NEA-5879).
 */
export function useGbpTotals(shopId, days = 365) {
	const key = shopId ? ['unify:gbp-totals', shopId, days] : null;
	const { data, error, isLoading } = useSWR(
		key,
		async () => {
			const client = await myShopClient();
			const to = new Date();
			const from = new Date(to.getTime() - days * 24 * 60 * 60 * 1000);
			const iso = (d) => d.toISOString().slice(0, 10);
			const { data } = await client.post('/api/insights', {
				id: [shopId],
				table: 'gmb',
				columns: [
					'date',
					'map_views_desktop',
					'map_views_mobile',
					'search_views_desktop',
					'search_views_mobile',
					'website_clicks',
					'phone_clicks',
					'directions_clicks'
				],
				from: iso(from),
				to: iso(to),
				sort: [{ column: 'date', order: 'asc' }]
			});
			return data;
		},
		{ revalidateOnFocus: false }
	);
	const rows = Array.isArray(data) ? data : Array.isArray(data?.rows) ? data.rows : [];
	const sum = (column) => rows.reduce((total, row) => total + (Number(row[column]) || 0), 0);
	const views = sum('search_views_desktop') + sum('search_views_mobile') + sum('map_views_desktop') + sum('map_views_mobile');
	const interactions = sum('website_clicks') + sum('phone_clicks') + sum('directions_clicks');
	return { views, interactions, hasData: rows.length > 0, error, isLoading };
}

const sumColumn = (rows, column) => rows.reduce((total, row) => total + (Number(row[column]) || 0), 0);

const pctChange = (current, previous) => {
	if (!previous) return current ? 100 : 0;
	return Number((((current - previous) / previous) * 100).toFixed(1));
};

// Google's GMB pipeline lands `map_views_*` first and `search_views_*` a few
// days later, so a shop's trailing rows arrive with maps populated and search
// still at 0. Plotted as-is, that's a chart-edge nosedive. The lag isn't fixed
// (median ~5 days across the estate, but with a long tail — some shops sit at
// 7+ for weeks), so a fixed shift under-corrects for shops on that tail.
// Instead, trim from the end of the returned series while both conditions
// hold: `search_views_* === 0` AND `map_views_* > 0`. Stop as soon as we hit
// a row with search > 0 — earlier legitimate zero-search days are preserved.
// Guarded: if no row in the window registers any search views, leave the
// series intact rather than trimming it to nothing (a shop with genuinely
// no search history — or a series that returned empty).
function trimTrailingUnsettledGbpRows(rows) {
	if (!Array.isArray(rows) || rows.length === 0) return rows;
	const searchViews = (r) => Number(r.search_views_desktop || 0) + Number(r.search_views_mobile || 0);
	const mapsViews = (r) => Number(r.map_views_desktop || 0) + Number(r.map_views_mobile || 0);
	const hasAnySettledSearch = rows.some((r) => searchViews(r) > 0);
	if (!hasAnySettledSearch) return rows;
	let end = rows.length;
	while (end > 0 && searchViews(rows[end - 1]) === 0 && mapsViews(rows[end - 1]) > 0) end -= 1;
	return end === rows.length ? rows : rows.slice(0, end);
}

/**
 * Two POST /api/insights calls in parallel — current window plus the
 * matching previous window for the delta pills. Returns the merged result.
 * Both windows share the SWR cache key so changing the date picker
 * invalidates the pair atomically.
 */
async function fetchRangeWithLookback(query, { from, to, lookbackFrom, lookbackTo }) {
	const client = await myShopClient();
	const [{ data: current }, { data: previous }] = await Promise.all([
		client.post('/api/insights', { ...query, from, to }),
		client.post('/api/insights', { ...query, from: lookbackFrom, to: lookbackTo })
	]);
	return { current: Array.isArray(current) ? current : [], previous: Array.isArray(previous) ? previous : [] };
}

/**
 * Google Business Profile daily insights for the active range plus the
 * matching previous period, used by the Results page's Google Local Listings
 * section (KPI tiles, profile-views line, search-vs-maps split, interactions
 * breakdown / time-series). Sources columns mirror the retailers-dashboard
 * BusinessProfile insights page so the numbers reconcile across products.
 */
export function useGbpInsights(shopId, range) {
	const key = shopId && range?.from ? ['unify:gbp', shopId, range.from, range.to] : null;
	const { data, error, isLoading } = useSWR(
		key,
		() =>
			fetchRangeWithLookback(
				{
					id: [shopId],
					table: 'gmb',
					columns: [
						'date',
						'map_views_desktop',
						'map_views_mobile',
						'search_views_desktop',
						'search_views_mobile',
						'website_clicks',
						'phone_clicks',
						'directions_clicks'
					],
					sort: [{ column: 'date', order: 'asc' }]
				},
				range
			),
		{ revalidateOnFocus: false }
	);
	const currentTrimmed = trimTrailingUnsettledGbpRows(data?.current ?? []);
	const previousTrimmed = trimTrailingUnsettledGbpRows(data?.previous ?? []);
	// Match window lengths post-trim so the delta pills compare same-shape
	// periods. Nearly always this just chops previous's tail — the previous
	// window is 30+ days older than current, so pipeline lag never touches it
	// and its natural trim is a no-op, while current typically loses ~5 days.
	// Skipped when either window is empty (a new shop with no lookback data
	// keeps its full current window; no comparison is drawn).
	let current = currentTrimmed;
	let previous = previousTrimmed;
	if (current.length > 0 && previous.length > 0) {
		const alignedLength = Math.min(current.length, previous.length);
		current = current.slice(0, alignedLength);
		previous = previous.slice(0, alignedLength);
	}
	const totalViews = (row) =>
		Number(row.search_views_desktop || 0) +
		Number(row.search_views_mobile || 0) +
		Number(row.map_views_desktop || 0) +
		Number(row.map_views_mobile || 0);
	const searchViews = (row) => Number(row.search_views_desktop || 0) + Number(row.search_views_mobile || 0);
	const mapsViews = (row) => Number(row.map_views_desktop || 0) + Number(row.map_views_mobile || 0);
	const interactions = (row) => Number(row.website_clicks || 0) + Number(row.phone_clicks || 0) + Number(row.directions_clicks || 0);
	const totals = (rows) => ({
		profileViews: rows.reduce((t, r) => t + totalViews(r), 0),
		searchViews: rows.reduce((t, r) => t + searchViews(r), 0),
		mapsViews: rows.reduce((t, r) => t + mapsViews(r), 0),
		interactions: rows.reduce((t, r) => t + interactions(r), 0),
		website: sumColumn(rows, 'website_clicks'),
		directions: sumColumn(rows, 'directions_clicks'),
		phone: sumColumn(rows, 'phone_clicks')
	});
	const currentTotals = totals(current);
	const previousTotals = totals(previous);
	return {
		isLoading,
		error,
		hasData: current.length > 0,
		totals: currentTotals,
		previousTotals,
		change: {
			profileViews: pctChange(currentTotals.profileViews, previousTotals.profileViews),
			searchViews: pctChange(currentTotals.searchViews, previousTotals.searchViews),
			mapsViews: pctChange(currentTotals.mapsViews, previousTotals.mapsViews),
			interactions: pctChange(currentTotals.interactions, previousTotals.interactions)
		},
		series: current.map((row) => ({
			date: row.date,
			total: totalViews(row),
			search: searchViews(row),
			maps: mapsViews(row),
			website: Number(row.website_clicks || 0),
			directions: Number(row.directions_clicks || 0),
			phone: Number(row.phone_clicks || 0)
		})),
		previousSeries: previous.map((row) => ({ date: row.date, total: totalViews(row) }))
	};
}

/**
 * Daily impressions + clicks for the Google Local Listings (organic) table.
 * Returns the per-day series and pre-summed totals + previous-period totals
 * so the Lovable design's KPI tiles render without extra reduce work.
 */
export function useLocalListingsInsights(shopId, range) {
	const key = shopId && range?.from ? ['unify:local-listings:daily', shopId, range.from, range.to] : null;
	const { data, error, isLoading } = useSWR(
		key,
		() =>
			fetchRangeWithLookback(
				{
					id: [shopId],
					table: 'local_listings',
					columns: ['date', 'impressions', 'clicks'],
					sort: [{ column: 'date', order: 'asc' }]
				},
				range
			),
		{ revalidateOnFocus: false }
	);
	const current = data?.current ?? [];
	const previous = data?.previous ?? [];
	const impressions = sumColumn(current, 'impressions');
	const clicks = sumColumn(current, 'clicks');
	const previousImpressions = sumColumn(previous, 'impressions');
	const previousClicks = sumColumn(previous, 'clicks');
	return {
		isLoading,
		error,
		hasData: current.length > 0,
		impressions,
		clicks,
		previousImpressions,
		previousClicks,
		change: {
			impressions: pctChange(impressions, previousImpressions),
			clicks: pctChange(clicks, previousClicks)
		},
		series: current
	};
}

async function fetchStoredQuery(category, queryId, params) {
	const client = await myShopClient();
	const path = category
		? `/api/insights/stored-query/${encodeURIComponent(category)}/${encodeURIComponent(queryId)}`
		: `/api/insights/stored-query/${encodeURIComponent(queryId)}`;
	const { data } = await client.get(path, { params });
	return data;
}

/**
 * Stored-query wrapper for the GET /api/insights/stored-query endpoint. SQL
 * lives in `interfaces/library/api-handlers/src/handlers/insights/stored-queries`
 * — the category/queryId combo selects which one runs. Passes the active
 * shop, range and any extra params straight through.
 */
export function useStoredQuery(shopId, category, queryId, { from, to, ...rest } = {}) {
	const key = shopId && queryId && from && to ? ['unify:stored-query', category, queryId, shopId, from, to, JSON.stringify(rest)] : null;
	const { data, error, isLoading } = useSWR(key, () => fetchStoredQuery(category, queryId, { shopIds: shopId, from, to, ...rest }), {
		revalidateOnFocus: false
	});
	return { data: Array.isArray(data) ? data : [], error, isLoading };
}

/**
 * Top-performing organic Local Listings products for the active range.
 * Backed by the local-listings/popular-products stored query — the same SQL
 * the retailers-dashboard Local Listings page uses for its Popular products
 * table, joining product_local_listings_weekly with analytics_events for
 * website-redirect counts.
 */
export function usePopularLocalListings(shopId, range) {
	return useStoredQuery(shopId, 'local-listings', 'popular-products', {
		from: range?.from,
		to: range?.to,
		includeRedirects: 'false'
	});
}

/**
 * Storefront analytics from the `analytics_events` table — totals,
 * distinct visitors, and a referrer breakdown for the active range. Scoped
 * to `domain = 'shopping'` (the value the shopping-service tracking layer
 * emits — see `channels/services/shopping-service/lib/tracking/index.js`).
 *
 * KPI labels reflect what the shopping-service genuinely emits:
 *   - distinct visitor_id values (unique visitors)
 *   - `product-page-view` events (product page views — fires on every
 *     storefront product detail render)
 *   - `product-page-reservation-started` / `product-page-order-started`
 *     (reservation/checkout funnel starts)
 *   - the five `shop-page-*-clicked` events (shop info clicks)
 *
 * We deliberately do NOT surface `product-website-redirect` here — that
 * endpoint is primarily the destination for Google Local Inventory Ads
 * click-throughs, not a storefront-specific interaction.
 *
 * Aggregation runs server-side in two stored queries (NEA-5858):
 *   - `storefront/overview-totals` — single-row counts, with lookback
 *   - `storefront/overview-traffic` — one row per distinct entry referrer
 *
 * Friendly referrer classification (`Within Near.st`, `Google Search` …)
 * stays in `classifyReferrer` on the client so the labels live in one
 * place; the SQL is referrer-host-agnostic.
 */
export function useStorefrontInsights(shopId, range) {
	const key = shopId && range?.from ? ['unify:storefront:stored', shopId, range.from, range.to] : null;
	const { data, error, isLoading } = useSWR(
		key,
		async () => {
			const [totals, traffic] = await Promise.all([
				fetchStoredQuery('storefront', 'overview-totals', {
					shopIds: shopId,
					from: range.from,
					to: range.to,
					// Lookback drives `change.*` pills; safe to omit when the
					// caller doesn't compute one (e.g. tests).
					...(range.lookbackFrom && range.lookbackTo ? { lookbackFrom: range.lookbackFrom, lookbackTo: range.lookbackTo } : {})
				}),
				fetchStoredQuery('storefront', 'overview-traffic', {
					shopIds: shopId,
					from: range.from,
					to: range.to
				})
			]);
			return { totals, traffic };
		},
		{ revalidateOnFocus: false }
	);

	// `overview-totals` returns `{ current: [row], lookback: [row] }` when the
	// lookback range was passed, otherwise a flat array of one row.
	const totalsCurrent = Array.isArray(data?.totals) ? data.totals[0] : data?.totals?.current?.[0];
	const totalsLookback = Array.isArray(data?.totals) ? undefined : data?.totals?.lookback?.[0];

	const visitors = Number(totalsCurrent?.visitors || 0);
	const previousVisitors = Number(totalsLookback?.visitors || 0);
	const productViews = Number(totalsCurrent?.product_views || 0);
	const previousProductViews = Number(totalsLookback?.product_views || 0);
	const reservationStarts = Number(totalsCurrent?.reservation_starts || 0);
	const previousReservationStarts = Number(totalsLookback?.reservation_starts || 0);
	const shopPageClicks = Number(totalsCurrent?.shop_page_clicks || 0);
	const previousShopPageClicks = Number(totalsLookback?.shop_page_clicks || 0);

	// One row per distinct entry-referrer URL — collapse to friendly labels
	// here. Internal NearSt-owned hosts are rebadged as "Within Near.st" by
	// `classifyReferrer` (not dropped), matching previous behaviour.
	const trafficRows = Array.isArray(data?.traffic) ? data.traffic : [];
	const referrerCounts = new Map();
	for (const row of trafficRows) {
		const label = classifyReferrer(row.referrer);
		referrerCounts.set(label, (referrerCounts.get(label) || 0) + Number(row.visitors || 0));
	}
	const traffic = [...referrerCounts.entries()].map(([label, value]) => ({ label, value })).sort((a, b) => b.value - a.value);

	return {
		isLoading,
		error,
		hasData: Number(totalsCurrent?.total_events || 0) > 0,
		visitors,
		productViews,
		reservationStarts,
		shopPageClicks,
		change: {
			visitors: pctChange(visitors, previousVisitors),
			productViews: pctChange(productViews, previousProductViews),
			reservationStarts: pctChange(reservationStarts, previousReservationStarts),
			shopPageClicks: pctChange(shopPageClicks, previousShopPageClicks)
		},
		traffic
	};
}

/**
 * Top viewed products on the storefront — counts `product-page-view`
 * events grouped by the barcode in the event attributes. Backed by the
 * `storefront/popular-products` stored query.
 */
export function useStorefrontPopularProducts(shopId, range) {
	return useStoredQuery(shopId, 'storefront', 'popular-products', { from: range?.from, to: range?.to });
}

/**
 * Batch-resolve barcode → title via the per-shop stock endpoint. The
 * shopping-service tracker only ships `barcode` in `product-page-view`
 * attributes — `title` isn't recorded on the event — so for production
 * data the popular-products stored query returns titles as null. This
 * hook fills them in by looking each barcode up against the shop's stock
 * (where the title is merged in by stock-gli-service from products + custom
 * products). N small GETs in parallel for the 6-or-so visible barcodes is
 * cheap; a single SWR key keeps the whole batch cached together.
 */
export function useStockTitles(shopId, barcodes) {
	const list = Array.isArray(barcodes) ? barcodes.filter(Boolean) : [];
	const key = shopId && list.length ? ['unify:stock-titles', shopId, list.join('|')] : null;
	const { data, isLoading } = useSWR(
		key,
		async () => {
			const client = await myShopClient();
			try {
				const { data: items } = await client.post(`/api/inventory/${shopId}/batch`, { barcodes: list });
				return Object.fromEntries((items || []).map((item) => [item.barcode, item.title || null]));
			} catch {
				return {};
			}
		},
		{ revalidateOnFocus: false }
	);
	return { titles: data || {}, isLoading };
}

/**
 * Map a raw `referrer` URL onto a friendly traffic-source label.
 *
 * NearSt-owned hosts (`near.st`, `*.near.st`) all collapse into a single
 * "Within Near.st" bucket. In production, the redirect chain for Google
 * Ads / GMC click-throughs lands shoppers on `near.st/...` before serving
 * the storefront page, which strips the original referrer — so the
 * majority of real traffic shows up as `near.st`. Honest framing: tell
 * the retailer that traffic came via the NearSt network, not which
 * external surface (which we don't reliably know).
 */
function classifyReferrer(referrer) {
	if (!referrer) return 'Direct / saved';
	try {
		const host = new URL(referrer).hostname.toLowerCase();
		if (host === 'near.st' || host.endsWith('.near.st')) return 'Within Near.st';
		if (host.includes('business.google') || host.includes('maps.google')) return 'Google Business Profile';
		if (host.includes('google.')) return 'Google Search';
		if (host.includes('instagram.com')) return 'Instagram';
		if (host.includes('facebook.com')) return 'Facebook';
		if (host.includes('tiktok.com')) return 'TikTok';
		return host.replace(/^www\./, '');
	} catch {
		return 'Direct / saved';
	}
}
